import React, { useState, useMemo, useEffect, FC } from 'react';
import cn from 'classnames';
import Shape from 'ati-ui-react/components/Shape';
import CardDisplayModel from 'models/CardDisplayModel';
import CardDataModel from 'models/CardDataModel';
import { CurrentPageType } from 'interfaces/IPagesStore';
import { IDisplayCard } from 'interfaces/IDisplayCard';
import { addMetric } from 'utils/addMetrics';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import AdvertItemDataModel from 'models/AdvertDataModel/AdvertItemDataModel';
import { useHistory } from 'react-router-dom';
import FavoritesButton from 'generic/FavoritesButton';
import { MiniCardProvider } from './MiniCardContext';
import LeasingBadge from './components/LeasingBadge';

import Image from './components/Image';
import Parameters from './components/Parameters';
import Badges from './components/Badges';
import Price from './components/Price';
import Geo from './components/Geo';
import Date from './components/Date';
import Views from './components/Views';
import OwnerInfo from './components/OwnerInfo';
import Name from './components/Name';
import Year from './components/Year';
import Mileage from './components/Mileage';
import {
  MiniCardStyles,
  Content,
  TopContainer,
  BottomContainer,
  RightContainer,
  CentralContainer,
  ItemParameters,
} from './MiniCardStyles';

// eslint-disable-next-line no-shadow
enum ShapeColors {
  NORMAL = 'white',
  HOVER_NORMAL = 'blue_light',
  HIGHLIGHTED = 'orange_light',
  HOVER_HIGHLIGHTED = 'orange',
}

const getShapeColor = (isHighlighted: boolean, isHovered: boolean): ShapeColors => {
  if (isHighlighted) {
    if (isHovered) {
      return ShapeColors.HOVER_HIGHLIGHTED;
    }
    return ShapeColors.HIGHLIGHTED;
  }
  if (isHovered) {
    return ShapeColors.HOVER_NORMAL;
  }
  return ShapeColors.NORMAL;
};

interface Props {
  advertData: any;
  accountData: any;
  page: CurrentPageType;
}

const MiniCard: FC<Props> = observer(({ advertData, accountData, page }) => {
  const {
    appStore: { isDesktop, isMobile, screenClassNames, isMobileApp },
  } = useStores();

  const [isHovered, setHovered] = useState(false);
  const history = useHistory();

  const cardProperties: IDisplayCard = {
    page,
    isHovered,
    hover: () => isDesktop && setHovered(true),
    leave: () => setHovered(false),
  };

  const display = useMemo(() => new CardDisplayModel(cardProperties), [cardProperties]);
  const data = useMemo(() => new CardDataModel(advertData, accountData), [advertData, accountData]);

  useEffect(() => {
    if (data.advert.leasingPrice) {
      addMetric('lizing-block-show-search');
    }
  }, []);

  const shapeColor = useMemo(() => getShapeColor(data.advert.isHighlighted, display.isHovered), [
    data.advert.isHighlighted,
    display.isHovered,
  ]);

  const itemParametersClassName = cn(data.advert.isComplexAdvert && 'complex-advert');
  const shapeClassNames = {
    shape: true,
    'no-border-radius': true,
    borders: true,
    hovered: display.isHovered,
  };

  const handleHover = () => display.hover();
  const handleLeave = () => display.leave();
  const handleClick = () => {
    if (!data.advert.isArchived && !data.advert.isDeleted) {
      if (isMobileApp) {
        history.push(data.advert.url);
      } else {
        window.open(data.advert.url, '_blank');
      }
    }
  };

  const renderDesktopItemParameters = (item: AdvertItemDataModel) => {
    return (
      <ItemParameters className={itemParametersClassName} key={item.type}>
        <div className="flex justify-between">
          <Name item={item} />
          <Year item={item} />
          <Mileage item={item} />
        </div>
        <Parameters item={item} itemType={item.type} />
      </ItemParameters>
    );
  };

  const renderMobileItemParameters = (item: AdvertItemDataModel) => {
    // @ts-ignore
    return (
      <ItemParameters className={itemParametersClassName} key={item.type}>
        <div className="flex justify-between">
          <div>
            <Name item={item} />
            {item.isFirstItem && (
              <div className="mobile-price-geo">
                <Price />
                <Geo />
              </div>
            )}
          </div>
          {item.isFirstItem && (
            // @ts-ignore
            <FavoritesButton className="favorites-button" advert={advertData} mobile />
          )}
        </div>
        <Parameters item={item} itemType={item.type} />
        <Year item={item} />
        <Mileage item={item} />
      </ItemParameters>
    );
  };

  const renderItemParameters = isMobile ? renderMobileItemParameters : renderDesktopItemParameters;

  const renderComplexAdvertParameters = () =>
    data.advert.items && data.advert.items.map((item) => renderItemParameters(item));

  const renderSingleAdvertParameters = () =>
    data.advert.item && renderItemParameters(data.advert.item);

  const renderParameters = data.advert.isComplexAdvert
    ? renderComplexAdvertParameters
    : renderSingleAdvertParameters;

  const renderDesktopCardContent = () => {
    return (
      <>
        <Image />
        <Content className={cn(screenClassNames, data.advertTypeClassNames)}>
          <TopContainer>
            <CentralContainer>
              {renderParameters()}
              <Badges />
            </CentralContainer>
            <RightContainer>
              <Price />
              {/* <LeasingBadge /> */}
              <Geo />
              {/* @ts-ignore */}
              <FavoritesButton className="favorites-button" advert={advertData} />
            </RightContainer>
          </TopContainer>
          <BottomContainer>
            <OwnerInfo />
            <div className="flex items-center">
              <Date className="date" />
              <Views />
            </div>
          </BottomContainer>
        </Content>
      </>
    );
  };

  const renderMobileCardContent = () => {
    return (
      <>
        <Image />
        <Content className={cn(screenClassNames, data.advertTypeClassNames)}>
          <TopContainer>
            <CentralContainer>
              {renderParameters()}
              <Badges />
              {/* <LeasingBadge /> */}
            </CentralContainer>
          </TopContainer>
          <BottomContainer>
            <OwnerInfo />
            <div className="flex items-center">
              <Date className="date" />
              <Views />
            </div>
          </BottomContainer>
        </Content>
      </>
    );
  };

  const renderCardContent = isMobile ? renderMobileCardContent : renderDesktopCardContent;

  return (
    <MiniCardProvider value={{ data, display }}>
      <MiniCardStyles onMouseEnter={handleHover} onMouseLeave={handleLeave} onClick={handleClick}>
        <Shape
          className={cn(shapeClassNames, screenClassNames)}
          color={shapeColor}
          shadow={display.isCabinetPage}
        >
          {renderCardContent()}
        </Shape>
      </MiniCardStyles>
    </MiniCardProvider>
  );
});

export default MiniCard;
